import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  FormControlLabel,
  Checkbox,
  Chip,
  IconButton,
  Paper,
  Tabs,
  Tab
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  PictureAsPdf as PdfIcon,
  Visibility as PreviewIcon,
  Download as DownloadIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  AutoAwesome as AutoAwesomeIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import API_CONFIG from '../../config/api';
import { apiFetch, getToken, getAuthenticatedUploadUrl } from '../../api/client';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { rotuloEscopoEditorDocumento } from '../../pages/contratos/utils/editorDocumentoEscopo';
import CamposTrDinamicosForm from './CamposTrDinamicosForm';
import { CAMPOS_TR_PADRAO } from '../../pages/contratos/utils/camposTrFluxo';
import {
  HUD_DIALOG_PAPER,
  HUD_DIALOG_TITLE,
  HUD_DIALOG_CONTENT,
  HUD_CTA_BUTTON,
  HUD_CARD,
} from './hudDialogStyles';

const BACKEND_URL = API_CONFIG.BACKEND_URL;
const STORAGE_KEY_HISTORICO_MOLDRAS = 'contratos_historico_molduras_tr';
const IMPORT_TIMEOUT_MS = 120000;

function fetchComTimeout(url, options = {}, timeoutMs = IMPORT_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return apiFetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(timer));
}

function carregarHistoricoMoldurasStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_HISTORICO_MOLDRAS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function salvarHistoricoMoldurasStorage(itens) {
  try {
    localStorage.setItem(STORAGE_KEY_HISTORICO_MOLDRAS, JSON.stringify(itens));
  } catch (e) {
    console.warn('Erro ao salvar histórico de molduras:', e);
  }
}

const CLAUSULAS_PADRAO = [
  { id: 1, titulo: '1. DO OBJETO', conteudo: '', fixo: false, ordem: 1 },
  { id: 2, titulo: '2. JUSTIFICATIVAS PARA CONTRATAÇÃO DO SERVIÇO', conteudo: '', fixo: false, ordem: 2 },
  { id: 3, titulo: '3. DESCRIÇÃO MINUCIOSA DO SERVIÇO', conteudo: '', fixo: false, ordem: 3 },
  { id: 4, titulo: '4. METODOLOGIAS DO SERVIÇO', conteudo: '', fixo: false, ordem: 4 },
  { id: 5, titulo: '5. PRAZO DE EXECUÇÃO DOS SERVIÇOS', conteudo: '', fixo: false, ordem: 5 },
  { id: 6, titulo: '6. REAJUSTE', conteudo: '', fixo: false, ordem: 6 },
  { id: 7, titulo: '7. QUALIFICAÇÕES TÉCNICAS EXIGIDAS', conteudo: '', fixo: false, ordem: 7 },
  { id: 8, titulo: '8. OBRIGAÇÕES DA EMPRESA CONTRATADA', conteudo: '', fixo: false, ordem: 8 },
  { id: 9, titulo: '9. OBRIGAÇÕES DA CONTRATANTE', conteudo: '', fixo: false, ordem: 9 },
  { id: 10, titulo: '10. GLOSAS', conteudo: '', fixo: false, ordem: 10 },
  { id: 11, titulo: '11. DA PROPOSTA', conteudo: '', fixo: false, ordem: 11 },
  { id: 12, titulo: '12. JULGAMENTO', conteudo: '', fixo: false, ordem: 12 },
  { id: 13, titulo: '13. A REALIZAÇÃO DO SERVIÇO', conteudo: '', fixo: false, ordem: 13 },
  { id: 14, titulo: '14. CONDIÇÕES DE PAGAMENTO', conteudo: '', fixo: false, ordem: 14 },
  { id: 15, titulo: '15. DISPOSIÇÕES FINAIS', conteudo: '', fixo: false, ordem: 15 }
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean']
  ]
};

const QUILL_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'bullet',
  'align',
  'link'
];

function normalizarHtmlQuill(value) {
  const v = String(value ?? '').trim();
  if (!v || v === '<p><br></p>' || v === '<p></p>') return '';
  return value;
}

const INSTRUMENTO_META = {
  tr: {
    label: 'Termo de Referência (TR)',
    labelCurto: 'TR',
    apiBase: '/api/contratos/templates/tr',
    tituloDefault: 'TERMO DE REFERÊNCIA',
    hint: 'Configure as cláusulas do TR. Use (+) e (−) para incluir ou remover seções. A IA melhora o texto de cada cláusula. O TR alimenta a criação da Minuta de contratação.',
  },
  minuta: {
    label: 'Minuta de Contratação',
    labelCurto: 'Minuta',
    apiBase: '/api/contratos/templates/minuta',
    tituloDefault: 'MINUTA DE CONTRATAÇÃO',
    hint: 'Instrumento de Minuta de contratação. Pode importar as cláusulas do TR da unidade como base e ajustar com (+) / (−) e IA.',
  },
  'minuta-aditivo': {
    label: 'Minuta para Aditivo',
    labelCurto: 'Minuta Aditivo',
    apiBase: '/api/contratos/templates/minuta-aditivo',
    tituloDefault: 'MINUTA DE ADITIVO',
    hint: 'Minuta específica para aditivo contratual. Use (+) / (−) nas cláusulas e IA para melhorar o texto. Pode partir do TR ou da Minuta de contratação.',
  },
};

function renumerarClausulas(lista) {
  return (lista || []).map((c, i) => {
    const rawTitulo = String(c.titulo || '').replace(/^\s*\d+\.\s*/, '').trim() || `Cláusula ${i + 1}`;
    return {
      ...c,
      id: c.id || i + 1,
      ordem: i + 1,
      titulo: `${i + 1}. ${rawTitulo}`,
    };
  });
}

export default function EditorTRModal({
  open,
  onClose,
  solicitacaoId = null,
  assuntoCI: assuntoCIProp = '',
  instrumentoTipo = 'tr',
  readOnly = false,
  processoId = null,
  fluxoId = null,
  escopo = null,
}) {
  const meta = INSTRUMENTO_META[instrumentoTipo] || INSTRUMENTO_META.tr;
  const apiBase = meta.apiBase;
  // Travas (+/− / Fixa) só no template global (Configuração). Instância = solicitação ou processo.
  const permitirGerenciarClausulas = !solicitacaoId && !processoId;
  const [loading, setLoading] = useState(false);
  const [importandoPdf, setImportandoPdf] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clausulas, setClausulas] = useState(CLAUSULAS_PADRAO);
  const [assuntoCI, setAssuntoCI] = useState(assuntoCIProp || '');
  const [aiLoadingIndex, setAiLoadingIndex] = useState(null);
  const [molduraFile, setMolduraFile] = useState(null);
  const [molduraUrl, setMolduraUrl] = useState(null);
  const [historicoMolduras, setHistoricoMolduras] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [arquivoIAForm, setArquivoIAForm] = useState(null);
  const [camposTrValores, setCamposTrValores] = useState({});
  const [camposTrSchema, setCamposTrSchema] = useState(CAMPOS_TR_PADRAO);
  const showCamposTab = solicitacaoId && instrumentoTipo === 'tr';
  const tabClausulas = showCamposTab ? 1 : 0;
  const tabMoldura = showCamposTab ? 2 : 1;
  const tabPreview = showCamposTab ? 3 : 2;

  const resolverUrlUpload = (urlOrPath) => {
    if (!urlOrPath) return null;
    const raw = String(urlOrPath);
    if (raw.includes('/uploads/')) {
      const idx = raw.indexOf('/uploads/');
      return getAuthenticatedUploadUrl(raw.slice(idx + '/uploads/'.length));
    }
    if (raw.startsWith('uploads/')) {
      return getAuthenticatedUploadUrl(raw.replace(/^uploads\//, ''));
    }
    return getAuthenticatedUploadUrl(raw);
  };

  const baixarDocumentoTR = async (formato) => {
    if (!solicitacaoId) return;
    try {
      const token = getToken();
      const res = await fetch(
        `${BACKEND_URL}/api/contratos/solicitacoes/${solicitacaoId}/tr/documento?formato=${formato}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setSnackbar({
          open: true,
          message: data.error || 'Erro ao baixar documento do TR. Faça login novamente.',
          severity: 'error'
        });
        return;
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      setSnackbar({
        open: true,
        message: 'Erro ao baixar documento do TR.',
        severity: 'error'
      });
    }
  };

  useEffect(() => {
    if (open) {
      setAssuntoCI(assuntoCIProp || '');
      setArquivoIAForm(null);
      setPreviewPdfUrl(null);
      setTabValue(0);
      carregarTemplate();
      setHistoricoMolduras(carregarHistoricoMoldurasStorage());
    }
  }, [open, solicitacaoId, processoId, assuntoCIProp, instrumentoTipo]);

  const importarTrDoAnexo = async () => {
    if (!solicitacaoId) return null;
    setImportandoPdf(true);
    try {
      let response;
      if (arquivoIAForm) {
        const formData = new FormData();
        formData.append('file', arquivoIAForm);
        formData.append('force', 'true');
        response = await fetchComTimeout(
          `/api/contratos/solicitacoes/${solicitacaoId}/tr/importar-anexo`,
          { method: 'POST', body: formData }
        );
      } else {
        response = await fetchComTimeout(
          `/api/contratos/solicitacoes/${solicitacaoId}/tr/importar-anexo`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({})
          }
        );
      }
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Erro ao importar TR do anexo.');
      }
      if (data.assuntoCI) setAssuntoCI(data.assuntoCI);
      if (Array.isArray(data.clausulas) && data.clausulas.length > 0 && (data.importado || arquivoIAForm)) {
        setClausulas(data.clausulas);
      }
      if (data.importado) {
        setSnackbar({
          open: true,
          message: 'Cláusulas importadas com sucesso.',
          severity: 'success'
        });
      } else if (data.motivo) {
        setSnackbar({
          open: true,
          message: data.motivo,
          severity: 'info'
        });
      }
      return data;
    } catch (error) {
      console.error('Erro ao importar TR do anexo:', error);
      const msg =
        error.name === 'AbortError'
          ? 'A importação do PDF demorou demais. O editor foi aberto — use "Importar do PDF" para tentar novamente.'
          : error.message || 'Não foi possível importar o TR do anexo.';
      setSnackbar({
        open: true,
        message: msg,
        severity: 'warning'
      });
      return null;
    } finally {
      setImportandoPdf(false);
    }
  };

  const carregarTemplate = async () => {
    setLoading(true);
    let deveImportar = false;
    try {
      if (solicitacaoId && instrumentoTipo === 'tr') {
        const response = await apiFetch(`/api/contratos/solicitacoes/${solicitacaoId}/tr`);
        if (response.ok) {
          const data = await response.json();
          if (data.clausulas && data.clausulas.length > 0) {
            setClausulas(renumerarClausulas(data.clausulas));
          }
          if (data.assuntoCI) setAssuntoCI(data.assuntoCI);
          if (data.campos && typeof data.campos === 'object') {
            setCamposTrValores(data.campos);
          }
          if (Array.isArray(data.camposTrSchema) && data.camposTrSchema.length) {
            setCamposTrSchema(data.camposTrSchema);
          }
          deveImportar = Boolean(data.importacaoDisponivel);
        }
        const resTemplate = await apiFetch(apiBase);
        if (resTemplate.ok) {
          const data = await resTemplate.json();
          if (data.moldura_url) setMolduraUrl(resolverUrlUpload(data.moldura_url));
        }
      } else if (processoId && (instrumentoTipo === 'minuta' || instrumentoTipo === 'minuta-aditivo')) {
        const response = await apiFetch(`/api/contratos/processos/${processoId}/minuta`);
        if (response.ok) {
          const data = await response.json();
          if (data.clausulas && data.clausulas.length > 0) {
            setClausulas(renumerarClausulas(data.clausulas));
          }
          if (data.titulo) setAssuntoCI(data.titulo);
        }
        const resTemplate = await apiFetch(apiBase);
        if (resTemplate.ok) {
          const data = await resTemplate.json();
          if (data.moldura_url) setMolduraUrl(resolverUrlUpload(data.moldura_url));
        }
      } else {
        const response = await apiFetch(apiBase);
        if (response.ok) {
          const data = await response.json();
          if (data.clausulas && data.clausulas.length > 0) {
            setClausulas(renumerarClausulas(data.clausulas));
          } else {
            setClausulas(renumerarClausulas(CLAUSULAS_PADRAO.map((c) => ({ ...c }))));
          }
          if (data.moldura_url) {
            setMolduraUrl(resolverUrlUpload(data.moldura_url));
          } else {
            setMolduraUrl(null);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar template:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar template',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }

    if (deveImportar) {
      importarTrDoAnexo();
    }
  };

  const importarBaseDoTR = async () => {
    try {
      setLoading(true);
      const response = await apiFetch('/api/contratos/templates/tr');
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Erro ao carregar TR');
      const lista = Array.isArray(data.clausulas) && data.clausulas.length
        ? data.clausulas
        : CLAUSULAS_PADRAO;
      setClausulas(renumerarClausulas(lista.map((c) => ({ ...c, id: undefined }))));
      setSnackbar({
        open: true,
        message: 'Cláusulas importadas do TR da unidade. Revise e salve a minuta.',
        severity: 'success',
      });
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Erro ao importar TR', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const adicionarClausula = (indexAfter = null) => {
    setClausulas((prev) => {
      const novas = [...prev];
      const item = {
        id: Date.now(),
        titulo: 'Nova cláusula',
        conteudo: '',
        fixo: false,
        ordem: 0,
      };
      if (indexAfter == null || indexAfter < 0) novas.push(item);
      else novas.splice(indexAfter + 1, 0, item);
      return renumerarClausulas(novas);
    });
  };

  const removerClausula = (index) => {
    setClausulas((prev) => {
      if (prev.length <= 1) {
        setSnackbar({ open: true, message: 'Mantenha ao menos uma cláusula.', severity: 'warning' });
        return prev;
      }
      if (!window.confirm('Remover esta cláusula?')) return prev;
      return renumerarClausulas(prev.filter((_, i) => i !== index));
    });
  };

  const sugerirComIA = async (index) => {
    const clausula = clausulas[index];
    if (!clausula || clausula.fixo || readOnly) return;

    setAiLoadingIndex(index);
    try {
      const response = await apiFetch('/api/ai/sugerir-clausula-tr', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tituloClausula: clausula.titulo,
          assuntoCI: assuntoCI || assuntoCIProp || meta.label,
          textoAtual: clausula.conteudo || '',
          tipoInstrumento: instrumentoTipo,
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Erro ao sugerir texto com IA.');
      }
      handleClausulaChange(index, 'conteudo', data.textoSugerido || '');
      setSnackbar({
        open: true,
        message: 'Texto da cláusula atualizado com sugestão da IA.',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro IA cláusula:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao usar IA na cláusula.',
        severity: 'error'
      });
    } finally {
      setAiLoadingIndex(null);
    }
  };

  const handleClausulaChange = (index, field, value) => {
    const atual = clausulas[index];
    if (!atual) return;

    // Solicitante / instância de processo: não pode travar/destravar cláusulas
    if (field === 'fixo' && !permitirGerenciarClausulas) return;

    if (field === 'conteudo') {
      const conteudoAtual = normalizarHtmlQuill(atual.conteudo);
      const conteudoNovo = normalizarHtmlQuill(value);
      if (conteudoAtual === conteudoNovo) return;
      value = conteudoNovo;
    } else if (atual[field] === value) {
      return;
    }

    setClausulas((prev) => {
      const novasClausulas = [...prev];
      novasClausulas[index] = {
        ...novasClausulas[index],
        [field]: value
      };
      return novasClausulas;
    });
  };

  const handleMolduraChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.match(/^image\/(png|jpeg|jpg)$/)) {
        setSnackbar({
          open: true,
          message: 'Apenas arquivos PNG ou JPG são permitidos',
          severity: 'error'
        });
        return;
      }
      setMolduraFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setMolduraUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const salvarMoldura = async () => {
    if (!molduraFile) return;

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('moldura', molduraFile);

      // FormData + Bearer via apiFetch (não definir Content-Type manualmente)
      const response = await apiFetch(`${apiBase}/molduras`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setMolduraUrl(resolverUrlUpload(data.moldura_path || data.moldura_url));
        const novoItem = { nome: molduraFile.name, data: new Date().toISOString() };
        const novoHistorico = [novoItem, ...carregarHistoricoMoldurasStorage()].slice(0, 50);
        setHistoricoMolduras(novoHistorico);
        salvarHistoricoMoldurasStorage(novoHistorico);
        setSnackbar({
          open: true,
          message: 'Moldura salva com sucesso',
          severity: 'success'
        });
        setMolduraFile(null);
      } else {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao salvar moldura');
      }
    } catch (error) {
      console.error('Erro ao salvar moldura:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao salvar moldura',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const salvarTemplate = async () => {
    if (readOnly) return;
    setSaving(true);
    try {
      if (solicitacaoId && instrumentoTipo === 'tr') {
        const response = await apiFetch(`/api/contratos/solicitacoes/${solicitacaoId}/tr`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clausulas, campos: camposTrValores })
        });
        if (response.ok) {
          setSnackbar({ open: true, message: 'TR da solicitação salvo com sucesso', severity: 'success' });
        } else {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || 'Erro ao salvar TR');
        }
      } else if (processoId && (instrumentoTipo === 'minuta' || instrumentoTipo === 'minuta-aditivo')) {
        const response = await apiFetch(`/api/contratos/processos/${processoId}/minuta`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clausulas, titulo: assuntoCI || assuntoCIProp || meta.tituloDefault })
        });
        if (response.ok) {
          setSnackbar({ open: true, message: 'Minuta do processo salva com sucesso', severity: 'success' });
        } else {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || 'Erro ao salvar minuta');
        }
      } else {
        const response = await apiFetch(apiBase, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clausulas })
        });
        if (response.ok) {
          setSnackbar({ open: true, message: `Template de ${meta.labelCurto} salvo com sucesso`, severity: 'success' });
        } else {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || 'Erro ao salvar template');
        }
      }
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao salvar',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const gerarPreviewPDF = async () => {
    setGeneratingPdf(true);
    try {
      const response = await apiFetch(`${apiBase}/gerar-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clausulas,
          solicitacaoId: solicitacaoId && instrumentoTipo === 'tr' ? solicitacaoId : undefined,
          titulo: assuntoCI || assuntoCIProp || meta.tituloDefault
        })
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setPreviewPdfUrl(resolverUrlUpload(data.pdf_path || data.pdf_url));
        setTabValue(tabPreview);
        setSnackbar({
          open: true,
          message: solicitacaoId && instrumentoTipo === 'tr'
            ? 'Preview gerado e TR PDF salvo na solicitação.'
            : 'Preview gerado com sucesso.',
          severity: 'success'
        });
      } else {
        throw new Error(data.error || 'Erro ao gerar PDF');
      }
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao gerar PDF. Verifique se a moldura foi configurada.',
        severity: 'error'
      });
    } finally {
      setGeneratingPdf(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={400} gap={2}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Carregando editor de {meta.labelCurto}...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: HUD_DIALOG_PAPER }}>
        <DialogTitle sx={{ ...HUD_DIALOG_TITLE, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>
              {solicitacaoId && instrumentoTipo === 'tr'
                ? 'Editar Termo de Referência (TR) da solicitação'
                : `Editar Template de ${meta.label}`}
            </Typography>
            <Typography variant="body2" sx={{ color: '#99f6e4', mt: 0.25 }}>
              Cláusulas · moldura · preview PDF · multiunidade
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={HUD_DIALOG_CONTENT}>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            {rotuloEscopoEditorDocumento({ escopo, processoId, fluxoId, solicitacaoId })}
          </Alert>
          {importandoPdf && (
            <Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={18} />}>
              Importando cláusulas do PDF anexo em segundo plano… Você já pode revisar o template abaixo.
            </Alert>
          )}
          <Tabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            sx={{
              mb: 2,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 700 },
              '& .Mui-selected': { color: '#0f766e !important' },
              '& .MuiTabs-indicator': { backgroundColor: '#0d9488', height: 3, borderRadius: 3 },
            }}
          >
            {showCamposTab && <Tab label="Campos do formulário" />}
            <Tab label="Cláusulas" />
            <Tab label="Moldura" />
            <Tab label="Preview PDF" />
          </Tabs>

          {showCamposTab && tabValue === 0 && (
            <Box sx={{ mb: 2 }}>
              <CamposTrDinamicosForm
                campos={camposTrSchema}
                valores={camposTrValores}
                onChange={setCamposTrValores}
                disabled={readOnly}
              />
            </Box>
          )}

          {tabValue === tabClausulas && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {meta.hint}
              </Typography>
              {readOnly && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Somente o Setor de Contratos (ou administradores) pode editar estes templates.
                </Alert>
              )}
              {permitirGerenciarClausulas && !readOnly && (instrumentoTipo === 'minuta' || instrumentoTipo === 'minuta-aditivo') && (
                <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={importarBaseDoTR}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Importar cláusulas do TR
                  </Button>
                </Stack>
              )}
              {solicitacaoId && instrumentoTipo === 'tr' && (
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<UploadFileIcon />}
                      component="label"
                      sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                      {arquivoIAForm ? 'Trocar PDF/Word p/ importar' : 'Anexar PDF/Word p/ importar'}
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={(e) => setArquivoIAForm(e.target.files?.[0] || null)}
                      />
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="warning"
                      startIcon={importandoPdf ? <CircularProgress size={16} /> : <PdfIcon />}
                      disabled={importandoPdf}
                      onClick={importarTrDoAnexo}
                      sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                      {importandoPdf ? 'Importando...' : 'Importar cláusulas'}
                    </Button>
                  </Stack>
                  {arquivoIAForm ? (
                    <Typography variant="caption" color="text.secondary">
                      Arquivo selecionado: {arquivoIAForm.name}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Sem ficheiro local: importa o TR já anexado na solicitação (se existir).
                    </Typography>
                  )}
                </Stack>
              )}
              {permitirGerenciarClausulas && !readOnly && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => adicionarClausula(null)}
                  sx={{
                    mb: 2,
                    textTransform: 'none',
                    fontWeight: 800,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    borderColor: 'rgba(13,148,136,0.45)',
                    color: '#0f766e',
                  }}
                  fullWidth
                >
                  Adicionar cláusula (+)
                </Button>
              )}
              <Stack spacing={2} sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                {clausulas.map((clausula, index) => (
                  <Paper key={clausula.id || index} variant="outlined" sx={{ ...HUD_CARD, border: '1px solid rgba(15,23,42,0.08)' }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} gap={1} flexWrap="wrap">
                      {permitirGerenciarClausulas && !readOnly ? (
                        <TextField
                          size="small"
                          value={clausula.titulo}
                          onChange={(e) => handleClausulaChange(index, 'titulo', e.target.value)}
                          sx={{ flex: 1, minWidth: 200, '& .MuiInputBase-input': { fontWeight: 800 } }}
                        />
                      ) : (
                        <Typography variant="subtitle2" fontWeight={800}>
                          {clausula.titulo}
                        </Typography>
                      )}
                      <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                        {!clausula.fixo && !readOnly && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={
                              aiLoadingIndex === index ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <AutoAwesomeIcon />
                              )
                            }
                            disabled={aiLoadingIndex !== null}
                            onClick={() => sugerirComIA(index)}
                            sx={{ textTransform: 'none' }}
                          >
                            {aiLoadingIndex === index ? 'Gerando...' : 'Melhorar com IA'}
                          </Button>
                        )}
                        {permitirGerenciarClausulas && !readOnly && (
                          <>
                            <IconButton size="small" color="primary" onClick={() => adicionarClausula(index)} title="Adicionar abaixo">
                              <AddIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => removerClausula(index)} title="Remover">
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                        {permitirGerenciarClausulas ? (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={clausula.fixo}
                                disabled={readOnly}
                                onChange={(e) => handleClausulaChange(index, 'fixo', e.target.checked)}
                                icon={<LockOpenIcon />}
                                checkedIcon={<LockIcon />}
                              />
                            }
                            label={clausula.fixo ? 'Fixa' : 'Editável'}
                          />
                        ) : (
                          <Chip
                            size="small"
                            icon={clausula.fixo ? <LockIcon /> : <LockOpenIcon />}
                            label={clausula.fixo ? 'Fixa (template)' : 'Editável'}
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        '& .quill': {
                          backgroundColor: clausula.fixo || readOnly ? 'rgba(0, 0, 0, 0.04)' : 'white'
                        },
                        '& .ql-editor': {
                          minHeight: '150px',
                          backgroundColor: clausula.fixo || readOnly ? 'rgba(0, 0, 0, 0.04)' : 'white',
                          color: clausula.fixo || readOnly ? 'rgba(0, 0, 0, 0.6)' : 'inherit'
                        },
                        '& .ql-toolbar': {
                          display: clausula.fixo || readOnly ? 'none' : 'block'
                        },
                        '& .ql-container': {
                          border: '1px solid rgba(0, 0, 0, 0.23)'
                        }
                      }}
                    >
                      <ReactQuill
                        theme="snow"
                        value={clausula.conteudo || ''}
                        onChange={(value) => handleClausulaChange(index, 'conteudo', value)}
                        readOnly={clausula.fixo || readOnly}
                        placeholder="Digite o conteúdo da cláusula..."
                        modules={QUILL_MODULES}
                        formats={QUILL_FORMATS}
                      />
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}

          {tabValue === tabMoldura && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Faça upload da moldura (imagem de fundo) que será usada no PDF do TR. Formatos aceitos: PNG ou JPG.
              </Typography>
              <Box
                sx={{
                  ...HUD_CARD,
                  mb: 2,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  alignItems: 'center',
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  borderColor: 'rgba(13,148,136,0.35)',
                  bgcolor: 'rgba(13,148,136,0.04)',
                }}
              >
                <input
                  accept="image/png,image/jpeg,image/jpg"
                  style={{ display: 'none' }}
                  id="moldura-upload"
                  type="file"
                  onChange={handleMolduraChange}
                />
                <label htmlFor="moldura-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Selecionar Moldura (PNG/JPG)
                  </Button>
                </label>
                {molduraFile && (
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={salvarMoldura}
                    disabled={saving}
                    sx={HUD_CTA_BUTTON}
                  >
                    {saving ? 'Salvando...' : 'Salvar Moldura'}
                  </Button>
                )}
              </Box>
              {molduraUrl && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview da Moldura:</Typography>
                  <Box
                    component="img"
                    src={molduraUrl}
                    alt="Moldura do TR"
                    sx={{
                      maxWidth: '100%',
                      height: 'auto',
                      border: '1px solid #ddd',
                      borderRadius: 1
                    }}
                  />
                </Box>
              )}

              {historicoMolduras.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Histórico de molduras já anexadas
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, maxHeight: 200, overflow: 'auto' }}>
                    {historicoMolduras.map((item, idx) => (
                      <Box component="li" key={idx} sx={{ mb: 0.5 }}>
                        <Typography variant="body2">
                          {item.nome}
                          {' — '}
                          <Typography component="span" variant="caption" color="text.secondary">
                            {item.data ? new Date(item.data).toLocaleString('pt-BR') : ''}
                          </Typography>
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              )}
            </Box>
          )}

          {tabValue === tabPreview && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Visualize o PDF gerado com a moldura e as cláusulas configuradas.
              </Typography>
              {!previewPdfUrl ? (
                <Alert severity="info">
                  Clique em "Gerar Preview" para visualizar o PDF.
                </Alert>
              ) : (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      href={previewPdfUrl}
                      download
                      sx={{ textTransform: 'none', mr: 1 }}
                    >
                      Baixar PDF
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<PreviewIcon />}
                      onClick={gerarPreviewPDF}
                      disabled={generatingPdf}
                      sx={{ textTransform: 'none' }}
                    >
                      {generatingPdf ? 'Gerando...' : 'Atualizar Preview'}
                    </Button>
                  </Box>
                  <Box
                    component="iframe"
                    src={previewPdfUrl}
                    sx={{
                      width: '100%',
                      height: '70vh',
                      border: '1px solid #ddd',
                      borderRadius: 1
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.75, flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={gerarPreviewPDF}
            disabled={generatingPdf || tabValue === tabPreview}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {generatingPdf ? 'Gerando...' : 'Gerar Preview'}
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={salvarTemplate}
            disabled={saving || readOnly}
            sx={HUD_CTA_BUTTON}
          >
            {saving ? 'Salvando...' : 'Salvar Template'}
          </Button>
          {solicitacaoId && (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PdfIcon />}
                onClick={() => baixarDocumentoTR('pdf')}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Baixar TR (PDF)
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => baixarDocumentoTR('docx')}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Baixar TR (Word)
              </Button>
            </>
          )}
          <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700, ml: { sm: 'auto' } }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificações */}
      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999 }}
        >
          {snackbar.message}
        </Alert>
      )}
    </>
  );
}

